import { hasActiveCategory } from './monthlyNeedCalculations.js';

/**
 * Catalogue de modules de filtration. `tamis` est une ligne fixe séparée (toujours en
 * descente de cuve, hors chaîne) — les 3 autres (`chainStage: true`) forment une chaîne de
 * traitement en série à ordre strictement imposé : micro-filtration → charbon actif → UV.
 * Cliquer sur un étage active automatiquement tous les étages en amont (cascadeEnable) ;
 * le désactiver désactive automatiquement les étages en aval (cascadeDisable). L'ensemble
 * sélectionné est donc toujours un préfixe contigu de la chaîne, jamais un trou.
 * `opex` (annuel) alimente le poste OPEX du ROI (cartouches, lampe UV...).
 */
export const FILTRATION_MODULES = [
  {
    id: 'tamis',
    label: 'Tamis grossier',
    description: 'Filtration des feuilles/débris en amont de la cuve (descente de gouttière) — recommandé quasi systématiquement',
    order: 0,
    chainStage: false,
    cost: { low: 80, high: 250 },
    opex: { low: 0, high: 20 },
  },
  {
    id: 'microfiltration',
    label: 'Micro-filtration',
    description: 'Retient les particules et sédiments les plus grossiers — 1er étage de la chaîne',
    order: 1,
    chainStage: true,
    cost: { low: 100, high: 250 },
    opex: { low: 20, high: 60 },
  },
  {
    id: 'charbon',
    label: 'Filtration charbon actif',
    description: 'Polluants, odeurs et turbidité — 2e étage, en série après la micro-filtration',
    order: 2,
    chainStage: true,
    cost: { low: 150, high: 280 },
    opex: { low: 20, high: 60 },
  },
  {
    id: 'uv',
    label: 'Désinfection UV',
    description: 'Traitement bactériologique — 3e étage, en fin de chaîne',
    order: 3,
    chainStage: true,
    cost: { low: 150, high: 450 },
    opex: { low: 60, high: 150 },
  },
];

const moduleById = (id) => FILTRATION_MODULES.find(m => m.id === id);

/** Les 3 étages de la chaîne, triés dans l'ordre de traitement (micro-filtration → charbon → UV). */
export const CHAIN_STAGES = FILTRATION_MODULES.filter(m => m.chainStage).sort((a, b) => a.order - b.order);

/** Filtration renforcée recommandée (non bloquant) si un usage sensible (lave-linge) est actif. */
export const shouldRecommendAdvancedFiltration = (usages) => hasActiveCategory(usages, 'washer');

/** Ajoute l'étage `id` et tous les étages qui le précèdent dans la chaîne (cascade d'activation). */
export const cascadeEnable = (id, selectedIds) => {
  const idx = CHAIN_STAGES.findIndex(s => s.id === id);
  const toAdd = CHAIN_STAGES.slice(0, idx + 1).map(s => s.id);
  return Array.from(new Set([...selectedIds, ...toAdd]));
};

/** Retire l'étage `id` et tous les étages en aval de la chaîne (cascade de désactivation). */
export const cascadeDisable = (id, selectedIds) => {
  const idx = CHAIN_STAGES.findIndex(s => s.id === id);
  const toRemove = new Set(CHAIN_STAGES.slice(idx).map(s => s.id));
  return selectedIds.filter(sid => !toRemove.has(sid));
};

/**
 * @param {Object} filtrationState - { moduleIds: string[], refined }
 */
export const computeFiltrationCost = (filtrationState) => {
  const breakdown = filtrationState.moduleIds.map(id => {
    const mod = moduleById(id);
    return { name: mod.label, low: mod.cost.low, high: mod.cost.high };
  });
  const low = breakdown.reduce((sum, r) => sum + r.low, 0);
  const high = breakdown.reduce((sum, r) => sum + r.high, 0);
  return { breakdown, low, high };
};

/** OPEX annuel (consommables/cartouches/lampe) des modules de filtration sélectionnés. */
export const computeFiltrationOpex = (filtrationState) => {
  return filtrationState.moduleIds.reduce((acc, id) => {
    const mod = moduleById(id);
    return { low: acc.low + mod.opex.low, high: acc.high + mod.opex.high };
  }, { low: 0, high: 0 });
};

/** Texte court pour l'en-tête replié de la section Filtration. */
export const summarizeFiltration = (filtrationState) => {
  const labels = filtrationState.moduleIds.map(id => moduleById(id).label);
  return labels.length > 0 ? labels.join(' + ') : 'Aucune filtration';
};
